import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { FileLabelResponseDto } from '@server/application/file-labels/dto/file-label.response.dto';
import { Col, Input, Row, Select, Typography } from 'antd';
import debounce from 'lodash/debounce';
import { LabelManagementModal } from '@/widgets/knowledge-base/LabelManagementModal';

interface KnowledgeBaseFileExplorerSearchProps {
  labels?: FileLabelResponseDto[];
  onLabelCreate?: (name: string) => Promise<FileLabelResponseDto>;
  onLabelDelete?: (id: string) => Promise<void>;
  searchTerm: string;
  onSearchTermChange: Dispatch<SetStateAction<string>>;
  selectedTags: string[];
  onSelectedTagsChange: Dispatch<SetStateAction<string[]>>;
}

export const KnowledgeBaseFileExplorerSearch = ({
  labels = [],
  onLabelCreate,
  onLabelDelete,
  searchTerm,
  onSearchTermChange,
  selectedTags,
  onSelectedTagsChange,
}: KnowledgeBaseFileExplorerSearchProps) => {
  const searchInputRef = useRef(null);
  const [isTagModalVisible, setIsTagModalVisible] = useState(false);

  return (
    <Row gutter={16} style={{ marginBottom: 16 }}>
      <Col flex="auto">
        <Input
          ref={searchInputRef}
          placeholder="Search files..."
          defaultValue={searchTerm}
          onChange={debounce(
            ({ target }) => onSearchTermChange(target.value),
            500,
          )}
          allowClear
        />
      </Col>
      <Col flex="0 0 35%">
        <Select
          mode="multiple"
          placeholder="Filter by labels"
          style={{ width: '100%' }}
          optionFilterProp="label"
          options={[
            ...labels.map((label) => ({
              label: label.name,
              value: label.id,
            })),
            {
              label: (
                <Typography.Link>
                  <PlusOutlined />{' '}
                  {labels?.length ? 'Manage labels' : 'Create a label'}
                </Typography.Link>
              ),
              value: 'manage-labels',
              className: 'manage-labels-option',
            },
          ]}
          value={selectedTags}
          onChange={(values) => {
            const realLabels = values.filter((v) => v !== 'manage-labels');
            onSelectedTagsChange(realLabels);
            if (values.includes('manage-labels')) {
              setIsTagModalVisible(true);
            }
          }}
          allowClear
          maxTagCount="responsive"
        />
      </Col>
      <LabelManagementModal
        open={isTagModalVisible}
        onClose={() => setIsTagModalVisible(false)}
        labels={labels}
        onCreateLabel={async (name) => {
          if (onLabelCreate) {
            return await onLabelCreate(name);
          }
          throw new Error('Label creation not available');
        }}
        onDeleteLabel={async (id) => {
          if (onLabelDelete) {
            await onLabelDelete(id);
            onSelectedTagsChange((prev) =>
              prev.filter((labelId) => labelId !== id),
            );
          }
        }}
      />
    </Row>
  );
};
